import {createRequiredContext} from '@raison/ui-kit';
import React, {ComponentProps, createElement, useLayoutEffect, useMemo, useRef, useState} from 'react';

class Accordion {
    options: Record<'shrink' | 'expand', KeyframeAnimationOptions> = {
        shrink: {
            duration: 200,
            easing: 'ease-out',
        },
        expand: {
            duration: 200,
            easing: 'ease-in',
        },
    };

    constructor(
        private readonly details: HTMLDetailsElement,
        private readonly summary: HTMLElement,
        private readonly content: {offsetHeight: number}
    ) {}

    private animation: null | Animation = null;

    shrink() {
        this.details.style.overflow = 'hidden';

        const fromHeight = `${this.details.offsetHeight}px`;
        const toHeight = `${this.summary.offsetHeight}px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.details.animate({height: [fromHeight, toHeight]}, this.options.shrink);
        this.animation.onfinish = this.onAnimationFinish.bind(this, false);
    }

    expand() {
        this.details.style.overflow = 'hidden';
        this.details.style.height = `${this.details.offsetHeight}px`;
        this.details.open = true;

        const fromHeight = `${this.details.offsetHeight}px`;
        const toHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

        if (this.animation) {
            this.animation.cancel();
        }

        this.animation = this.details.animate({height: [fromHeight, toHeight]}, this.options.expand);
        this.animation.onfinish = this.onAnimationFinish.bind(this, true);
    }

    private onAnimationFinish(open: boolean) {
        this.animation = null;
        this.details.open = open;
        this.details.style.height = this.details.style.overflow = '';
    }
}

const {AccordionContextProvider, useAccordionContext} = createRequiredContext('AccordionContext')<{
    focusedId: number;
    setFocusedId: (id: number) => void;
    nextIdRef: {current: number};
}>();

export function AccordionGroup({children}: React.PropsWithChildren<{}>) {
    const nextIdRef = useRef(0);
    const [focusedId, setFocusedId] = useState(-1);
    return createElement(AccordionContextProvider, {value: {focusedId, setFocusedId, nextIdRef}, children});
}

export function useAccordionGroup() {
    const {focusedId, setFocusedId, nextIdRef} = useAccordionContext();

    const myId = useMemo(() => nextIdRef.current++, []);
    const isOpen = myId === focusedId;
    const onToggle = (open: boolean) => {
        if (open) {
            setFocusedId(myId);
        } else {
            setFocusedId(-1);
        }
    };
    return [isOpen, onToggle] as const;
}

export function useAccordion(open: boolean, onToggle: (open: boolean) => void) {
    const [detailsEl, setDetailsEl] = useState<null | HTMLDetailsElement>(null);
    const [summaryEl, setSummaryEl] = useState<null | HTMLElement>(null);
    const [contentEl, setContentEl] = useState<null | {offsetHeight: number}>(null);

    const accordion = useMemo(() => {
        if (detailsEl && summaryEl && contentEl) {
            return new Accordion(detailsEl, summaryEl, contentEl);
        }
    }, [detailsEl, summaryEl, contentEl]);

    useLayoutEffect(() => {
        if (!accordion) return;
        if (open) {
            accordion.expand();
        } else {
            accordion.shrink();
        }
    }, [open]);

    const detailsProps: ComponentProps<'details'> = {
        // because of bad @types/react
        ref: setDetailsEl as (el: null | HTMLElement) => void,
    };

    const summaryProps: ComponentProps<'summary'> = {
        ref: setSummaryEl,
        onClick(event) {
            event.preventDefault();
            onToggle(!open);
        },
    };

    const contentProps = {
        ref: setContentEl,
    };

    return {detailsProps, summaryProps, contentProps};
}
